import axios from "axios";
import { apiBaseUrl } from "./backend";
import { logError } from "../utils/errors/logger";
import { getProductListFragment } from "./fragments";

export const fetchCategory = async ({ catId, showChild = true }) => {
  let query = `query getCategoryDetails($filters: CategoryFilterInput) {
    categories (filters: $filters) {
      total_count
      page_info { page_size current_page total_pages }
      items {
        id name component_id
        children {
          uid name image url_path component_id
          children {
            id name image url_path component_id
          }
        }
      }
    }
  }`;

  if (!showChild) {
    query = `query getCategoryDetails($filters: CategoryFilterInput) {
      categories(filters: $filters) {
        total_count
        items {
          id level name url_key url_path description children_count
          children {
            id level name url_path url_key image
          }
        }
      }
    }`;
  }

  try {
    // Serialize the variables object for GET requests
    // Ensures proper query parameter formatting
    const axiosRes = await axios.get(apiBaseUrl(), {
      params: {
        query,
        variables: JSON.stringify({ filters: { ids: { in: [catId] } } }),
      },
      paramsSerializer: { indexes: null },
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, message: "fetchCategory", info: { catId, showChild } });
    return { data: [], errors: error };
  }
};

export const fetchFeaturedItems = async (type, coupon) => {
  const productFragment = getProductListFragment();
  const variables = JSON.stringify({
    productFilter: {
      [type]: {
        eq: 1,
      },
      price: { from: 0.01 },
    },
    sort: {
      name: "ASC",
    },
    PageSize: 4,
  });

  const query = `query showOnBasketProducts ($productFilter: ProductAttributeFilterInput $sort: ProductAttributeSortInput $pageSize: Int) {
    products: products (sort: $sort pageSize: $pageSize filter: $productFilter specialCoupon: "${coupon}") {
      total_count items { ...Products }
    }
  } ${productFragment}`;

  try {
    const axiosRes = await axios.get(apiBaseUrl(), {
      params: { query, variables, operationName: "categoryList" },
      paramsSerializer: { indexes: null },
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, message: "fetchFeaturedItems", info: { type, coupon } });
    return { data: [], errors: error };
  }
};

export const fetchSubCategories = async (parentCatId) => {
  try {
    const axiosRes = await axios.post(apiBaseUrl(), {
      variables: { filters: { ids: { in: [parentCatId] } } },
      query: `query GetSubCategoryData ($filters: CategoryFilterInput) {
        categories (filters: $filters) {
          items {
            id name url_path image description cat_short_description children_count
            breadcrumbs { category_id category_name category_url_path }
            children { id name url_path image }
          }
        }
      }`,
    });
    return axiosRes.data;
  } catch (error) {
    logError({ error, message: "fetchSubCategories", info: { parentCatId } });
    return { data: [], errors: error };
  }
};

export const fetchFeaturedCategories = async (featuredTypes) => {
  try {
    const url = apiBaseUrl();
    const query = `query GetFeaturedCategories($featuredTypes: [NetsolFeaturedTypeInput!]!) {
      getFeaturedCategories(featuredTypes: $featuredTypes) {
        featureTypeCategory pageSize items { name url image }
      }
    }`;

    const encodedQuery = encodeURIComponent(query);
    const variables = encodeURIComponent(JSON.stringify({ featuredTypes }));
    const finalUrl = `${url}?query=${encodedQuery}&variables=${variables}`;
    const axiosRes = await axios.get(finalUrl);
    return axiosRes?.data?.data;
  } catch (error) {
    return { data: {}, errors: error };
  }
};

export const fetchFeaturedProducts = async (featuredTypes) => {
  try {
    const query = `query GetFeaturedProducts($featuredTypes: [NetsolFeaturedTypeInput!]!) {
      getFeaturedProducts(featuredTypes: $featuredTypes) {
        featureTypeProduct pageSize items { sku }
      }
    }`;

    const url = apiBaseUrl();
    const encodedQuery = encodeURIComponent(query);
    const variables = encodeURIComponent(JSON.stringify({ featuredTypes }));
    const finalUrl = `${url}?query=${encodedQuery}&variables=${variables}`;
    const axiosRes = await axios.get(finalUrl);
    return axiosRes?.data?.data;
  } catch (error) {
    return { data: {}, errors: error };
  }
};

export const fetchCategoryDetails = async ({
  catId,
  limited = false,
  withDesc = false,
  onlyList = false,
  showChild = true,
  paginated = false,
  showContent = true,
  withoutCount = false,
  withShortDesc = false,
}) => {
  const shortDesc = withShortDesc ? "cat_short_description" : "";
  const children = showChild
    ? "children { id name image url_path component_id }"
    : "";
  const breadcrumbs =
    "breadcrumbs { category_id category_name category_level category_url_path category_url_key }";

  let fields = `items { id name description ${shortDesc} ${breadcrumbs}
    children { id name description image url_path component_id ${children} }
  }`;

  if (onlyList) {
    fields = `items { id name children { id name image url_path }}`;
  }

  if (limited) {
    const desc = withDesc ? "component_id description" : "";
    fields = `items { id name cat_short_description ${desc} ${breadcrumbs} }`;
  }

  if (paginated) {
    fields = `items { id name component_id cat_short_description
      children { uid name image url_path component_id children { id name image url_path component_id } }
    }`;

    if (!withoutCount) {
      fields += " total_count page_info { page_size current_page total_pages }";
    }

    if (!showContent) {
      fields = "items { id name cat_short_description }";
    }
  }

  try {
    const axiosRes = await axios.get(apiBaseUrl(), {
      paramsSerializer: { indexes: null }, // Prevent array indices from being added to query parameters
      params: {
        variables: JSON.stringify({ filters: { ids: { in: [catId] } } }),
        query: `query getCategoryDetails ($filters: CategoryFilterInput) {
          categories(filters: $filters) { ${fields} }
        }`,
      },
    });
    return { error: null, data: axiosRes?.data?.data || {} };
  } catch (error) {
    logError({
      error,
      message: "fetchCategoryDetails",
      info: {
        catId,
        limited,
        withDesc,
        showChild,
        paginated,
        showContent,
        withShortDesc,
      },
    });
    return { data: [], error: error.message };
  }
};

export const getFeaturedDeals = async (coupon = "") => {
  const productFragment = getProductListFragment("home");
  try {
    const query = `query FeaturedAndSeasonalProducts(
        $featuredFilter: ProductAttributeFilterInput,
        $featuredSort: ProductAttributeSortInput,
        $featuredPageSize: Int
      ) {
        featured_products: products(
          filter: $featuredFilter,
          sort: $featuredSort,
          pageSize: $featuredPageSize
          specialCoupon: "${coupon}"
        ) {
          total_count items { ...Products }
        }
      } ${productFragment}`;

    const axiosRes = await axios.get(apiBaseUrl(), {
      params: {
        query,
        variables: JSON.stringify({
          featuredFilter: {
            price: { from: 0.01 },
            featured_product: { eq: 1 },
          },
          featuredPageSize: 24,
          featuredSort: { featured_product_sort_order: "ASC" },
        }),
      },
      paramsSerializer: {
        indexes: null, // Ensures proper serialization
      },
    });

    if (axiosRes.data.errors) {
      logError({
        info: { coupon },
        message: "getFeaturedDeals",
        error: axiosRes.data.errors,
      });
    }
    return { data: axiosRes.data.data, error: null };
  } catch (error) {
    logError({ error, info: { coupon }, message: "getFeaturedDeals" });
    return { data: [], error: error.message };
  }
};
